import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import client from 'client';
import ModalSubscriptionsPaywalls from './ModalSubscriptionsPayWalls';
import AuthActions from '../../stores/loadz/redux/Auth/actions';
import { useDispatch } from 'react-redux';

const SubscriptionPaywallContext = createContext({
  paywall: null,
  notifyPastDue: () => false,
  blockIfLocked: () => false,
  blockNewJob: () => false,
  openPaywall: () => {},
  isPaywallResponse: () => false,
});

const LOGIN_SHOWN_KEY = 'subscription_paywall_login_shown';

/**
 * Propósito: Detecta respuestas 402/paywall (unpaid, restricted o locked).
 */
export const isPaywallResponse = (data) => {
  /**
   * Output:
   * @returns boolean — true si el payload es de paywall
   */
  return (
    data?.status === 'incomplete_expired' ||
    data?.status === 'unpaid' ||
    data?.phase === 'past_due' ||
    data?.phase === 'restricted' ||
    data?.phase === 'locked'
  );
};

export const SubscriptionPaywallProvider = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const user = useSelector((state) => state.loadz_auth?.AuthReducer?.user);
  const compacctnum = user?.company?.compacctnum;
  const userType = user?.user_type;
  const [paywall, setPaywall] = useState(null);
  const [open, setOpen] = useState(false);
  const [lockMode, setLockMode] = useState(false);

  const isPaymentRoute = location.pathname.includes('/subscriptions/apply-payment-view');

  /**
   * Propósito: Abre el modal; forcedLock quita el close (Forced Pay Now / lock).
   */
  const openPaywall = useCallback((forcedLock = false) => {
    if (forcedLock) {
      setLockMode(true);
    }
    setOpen(true);
    /**
     * Output:
     * Side effects — Modal abierto; lockMode si forcedLock
     */
  }, []);

  /**
   * Propósito: Muestra el aviso al editar. Restricted/locked no se pueden cerrar.
   */
  const notifyPastDue = useCallback(() => {
    if (paywall?.phase === 'locked' || paywall?.phase === 'restricted') {
      openPaywall(true);
      /**
       * Output:
       * @returns boolean — true: Forced Pay Now / lock
       */
      return true;
    }
    if (paywall?.phase === 'past_due') {
      openPaywall(false);
      /**
       * Output:
       * @returns boolean — false: reminder cerrable
       */
      return false;
    }
    /**
     * Output:
     * @returns boolean — false: sin paywall
     */
    return false;
  }, [paywall, openPaywall]);

  const blockIfLocked = useCallback(() => {
    if (paywall?.phase === 'locked') {
      openPaywall(true);
      return true;
    }
    return false;
  }, [paywall, openPaywall]);

  /**
   * Propósito: Bloquea create job en past_due/restricted/locked; Forced Pay Now desde día 10.
   */
  const blockNewJob = useCallback(() => {
    if (paywall?.phase === 'past_due' || paywall?.phase === 'restricted' || paywall?.phase === 'locked') {
      openPaywall(paywall.phase === 'locked' || paywall.phase === 'restricted');
      /**
       * Output:
       * @returns boolean — true: no crear job
       */
      return true;
    }
    /**
     * Output:
     * @returns boolean — false: se puede crear
     */
    return false;
  }, [paywall, openPaywall]);

  useEffect(() => {
    if (!compacctnum || userType === 'super-admin') {
      setPaywall(null);
      return;
    }

    let cancelled = false;

    const loadPaywall = async () => {
      try {
        const response = await client.get(`/subscription/subscription-company/${compacctnum}`);
        const subscription = Array.isArray(response.data) ? response.data[0] : response.data;
        if (cancelled || !subscription) {
          return;
        }

        setPaywall(subscription);

        if (subscription.phase === 'locked' && !isPaymentRoute) {
          localStorage.removeItem('token');
          dispatch(AuthActions.logout());
          history.replace(
            `/login?paywall=locked&compacctnum=${encodeURIComponent(compacctnum)}`
          );
          return;
        }

        if (subscription.phase === 'restricted' && !isPaymentRoute) {
          setLockMode(true);
          setOpen(true);
          return;
        }

        if (subscription.phase === 'past_due' && !sessionStorage.getItem(LOGIN_SHOWN_KEY)) {
          sessionStorage.setItem(LOGIN_SHOWN_KEY, '1');
          setOpen(true);
        }
      } catch (error) {
        console.log(error);
      }
    };

    loadPaywall();

    return () => {
      cancelled = true;
    };
  }, [compacctnum, userType, isPaymentRoute, dispatch, history]);

  const value = useMemo(
    () => ({
      paywall,
      notifyPastDue,
      blockIfLocked,
      blockNewJob,
      openPaywall,
      isPaywallResponse,
    }),
    [paywall, notifyPastDue, blockIfLocked, blockNewJob, openPaywall]
  );

  return (
    <SubscriptionPaywallContext.Provider value={value}>
      {children}
      <ModalSubscriptionsPaywalls
        open={open}
        setSubscriptionsPaywalls={setOpen}
        compacctnum={compacctnum}
        phase={paywall?.phase}
        daysRemaining={paywall?.daysRemaining}
        paymentUrl={paywall?.paymentUrl}
        lockMode={lockMode}
      />
    </SubscriptionPaywallContext.Provider>
  );
};

export const useSubscriptionPaywall = () => useContext(SubscriptionPaywallContext);

export default SubscriptionPaywallContext;
